import { useEffect, useState } from 'react'
import { useAppSelector } from '../../../../../store/hooks'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useGetAccountQuery } from '../../../../../store/accountUserSettings/accountUserSettings'
import { type CheckpointRequest, useCreateCheckpointMutation, useGetCheckpointQuery, useUpdateCheckpointMutation } from '../../../../../store/sync/sync'
import { getAccountCRMIntegrationConfig } from '../../../../../models/Account'
import { type IntegrationServiceId } from '../../../../../common/integrations'
import { type SalesforceSettingsFormValues } from './models'

export const useSalesforceBasedContactSync = (serviceId: IntegrationServiceId) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: account, isFetching: isAccountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: 10 })
  const checkpointRequest = {
    accountId,
    level: 'user',
    connector: serviceId,
    direction: 'inbound',
    object: 'contact_operation_hashed',
  } as CheckpointRequest
  const { data: checkpoint, isFetching: isGetCheckpointFetching } = useGetCheckpointQuery(checkpointRequest, { refetchOnMountOrArgChange: 10 })
  const [createCheckpoint] = useCreateCheckpointMutation()
  const [updateCheckpoint] = useUpdateCheckpointMutation()
  const [isSyncDisabled, setIsSyncDisabled] = useState<boolean>(true)
  const [autoSync, setAutoSync] = useState<boolean | null>(false)
  const [isUpdating, setIsUpdating] = useState<boolean>(false)

  useEffect(() => {
    if (account && account.data) {
      const serviceIntegration = getAccountCRMIntegrationConfig(account.data, serviceId)
      if (!serviceIntegration) {
        setIsSyncDisabled(false)
        return
      }
      setIsSyncDisabled(serviceIntegration.isSyncDisabled ?? false)
    }
  }, [account, serviceId])

  useEffect(() => {
    setAutoSync(checkpoint?.data?.active ?? null)
  }, [checkpoint])

  const save = async (data: SalesforceSettingsFormValues) => {
    try {
      setIsUpdating(true)
      if (autoSync == null) {
        if (data.inbound.contact.autoSync) {
          await createCheckpoint(checkpointRequest).unwrap()
        }
      } else {
        await updateCheckpoint({ ...checkpointRequest, active: data.inbound.contact.autoSync }).unwrap()
      }
    } catch (e) {
      console.error(e)
    } finally {
      setIsUpdating(false)
    }
  }

  const saveAutoSync = async (active: boolean) => {
    try {
      if (autoSync == null) {
        if (active) await createCheckpoint(checkpointRequest).unwrap()
      } else {
        await updateCheckpoint({ ...checkpointRequest, active }).unwrap()
      }
    } catch (e) {
      console.error(e)
    }
  }

  return {
    autoSync,
    isSyncDisabled,
    isAccountFetching,
    isFetching: !isUpdating && (isGetCheckpointFetching || isAccountFetching),
    isSaving: isUpdating,
    save,
    saveAutoSync,
  }
}
